import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { ownerBenefits, tenantBenefits } from "@/data/content";

export default function Benefits() {
  const [tab, setTab] = useState("owner");
  const items = tab === "owner" ? ownerBenefits : tenantBenefits;

  return (
    <section id="vantaggi" data-testid="benefits-section" className="bg-ink text-white py-24 lg:py-36 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[420px] h-[420px] rounded-full bg-azure/10 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6 }}
              className="eyebrow text-azure mb-6"
            >
              I vantaggi
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7 }}
              className="font-serif font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.12] max-w-2xl"
            >
              I motivi per cui affidare <span className="italic text-azure">il tuo immobile</span> a noi
            </motion.h2>
          </div>

          <div className="inline-flex rounded-full border border-white/15 bg-white/5 p-1 self-start" data-testid="benefits-tabs">
            <button
              data-testid="benefit-tab-owners-btn"
              onClick={() => setTab("owner")}
              className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                tab === "owner" ? "bg-azure text-white" : "text-white/60 hover:text-white"
              }`}
            >
              Per il proprietario
            </button>
            <button
              data-testid="benefit-tab-tenants-btn"
              onClick={() => setTab("tenant")}
              className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                tab === "tenant" ? "bg-azure text-white" : "text-white/60 hover:text-white"
              }`}
            >
              Per l'inquilino
            </button>
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {items.map((b, i) => (
              <motion.article
                key={b.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
                whileHover={{ y: -5 }}
                data-testid={`benefit-card-${tab}-${i + 1}`}
                className="group rounded-2xl border border-white/10 bg-white/[0.04] p-7 hover:border-azure/50 hover:bg-white/[0.07] transition-[border-color,background-color] duration-300"
              >
                <CheckCircle2 size={26} className="text-azure group-hover:text-gold transition-colors duration-500" />
                <h3 className="mt-4 font-semibold text-lg leading-snug">{b.title}</h3>
                <p className="mt-3 text-sm text-white/55 leading-relaxed">{b.desc}</p>
              </motion.article>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
